/**
 * Browser-side card redaction — a MIRROR of `packages/determinism/src/card.ts`.
 *
 * The playground redacts a typed card before the turn bubble renders, before
 * the .md export, and before the request leaves the browser; the server's
 * intake redaction remains the backstop. It is a copy rather than an import
 * because the workspace packages compile to CommonJS and the vite production
 * bundle cannot statically resolve named exports through `__exportStar` —
 * pipeline #262 failed on exactly that. Web already keeps its own redaction
 * lib for eval exports (`eval-import/redact.ts`); this follows that
 * precedent. Tests on BOTH copies pin the same cases, so drift shows as a
 * failure, not as a silent difference.
 *
 * Detection is deliberately narrow: card length (13-16 digits), a real card
 * prefix (3/4/5/6), and a passing Luhn check — order ids, phone numbers and
 * tracking codes pass through byte for byte.
 */

const CARD_NUMBER_CANDIDATE = /\b[3-6](?:[ -]?\d){12,15}\b/g;

function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let value = digits.charCodeAt(i) - 48;
    if (double) {
      value *= 2;
      if (value > 9) value -= 9;
    }
    sum += value;
    double = !double;
  }
  return sum % 10 === 0;
}

export interface CardRedaction {
  text: string;
  redacted: number;
}

/** Remove every validated card number, keeping the last four in a marker. */
export function redactCardNumbers(text: string): CardRedaction {
  let redacted = 0;
  const replaced = text.replace(CARD_NUMBER_CANDIDATE, (match) => {
    const digits = match.replace(/[ -]/g, '');
    if (digits.length < 13 || digits.length > 16 || !luhnValid(digits)) return match;
    redacted += 1;
    return `[card number ending ${digits.slice(-4)} — removed for security]`;
  });
  return { text: redacted > 0 ? replaced : text, redacted };
}
